import { useMutation, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { createTier } from "../graphql/mutations/createTier";
import { deleteTier } from "../graphql/mutations/deleteTier";
import { updateTier } from "../graphql/mutations/updateTier";
import { listTiers } from "../graphql/queries/listTiers";

export const useTiers = () => {
  const { data, loading, error, refetch } = useQuery(listTiers, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [executeCreate, { loading: creating }] = useMutation(createTier);
  const [executeUpdate, { loading: updating }] = useMutation(updateTier);
  const [executeDelete, { loading: deleting }] = useMutation(deleteTier);

  const tiers = useMemo(() => {
    if (!data?.listTiers?.items) return [];
    return data.listTiers.items
      .filter((t) => !t.deletedAt)
      .sort((a, b) => (a.entryThresholdMinor || 0) - (b.entryThresholdMinor || 0));
  }, [data]);

  const totalCount = data?.listTiers?.totalCount || 0;

  const handleCreateTier = async (input) => {
    const result = await executeCreate({ variables: { input } });
    await refetch();
    return result;
  };

  const handleUpdateTier = async (id, input) => {
    const result = await executeUpdate({ variables: { id, input } });
    await refetch();
    return result;
  };

  const handleDeleteTier = async (id) => {
    const result = await executeDelete({ variables: { id } });
    await refetch();
    return result;
  };

  // Save only changed/new tiers and delete removed ones in a single batch
  const saveTiers = async (tiersToSave, tiersToDelete = []) => {
    const results = [];

    // Delete removed tiers (no refetch between each)
    for (const tierId of tiersToDelete) {
      const result = await executeDelete({ variables: { id: tierId } });
      results.push(result);
    }

    for (const tier of tiersToSave) {
      const input = {
        name: tier.name,
        entryThresholdMinor: Math.round(Number(tier.entryThresholdMinor)),
        multiplier: Number(tier.multiplier),
        benefits: [],
      };

      const isNew = !tier.id || tier.id.startsWith("tier-new-");

      if (isNew) {
        // Create new tier
        const result = await executeCreate({ variables: { input } });
        results.push(result);
      } else {
        // Check if tier actually changed compared to server state
        const serverTier = tiers.find((st) => st.id === tier.id);
        const hasChanged =
          !serverTier ||
          serverTier.name !== input.name ||
          (serverTier.entryThresholdMinor || 0) !== input.entryThresholdMinor ||
          (serverTier.multiplier || 0) !== input.multiplier;

        if (hasChanged) {
          const result = await executeUpdate({
            variables: { id: tier.id, input },
          });
          results.push(result);
        }
      }
    }

    await refetch();
    return results;
  };

  return {
    tiers,
    totalCount,
    loading,
    error,
    creating,
    updating,
    deleting,
    saving: creating || updating,
    refetch,
    handleCreateTier,
    handleUpdateTier,
    handleDeleteTier,
    saveTiers,
  };
};
